import React, { useState, useEffect, useTransition } from "react";
import { MapPinIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";

interface Props {
  tempLocation: string;
  setTempLocation: (value: string) => void;
  onChange?: (value: string) => void;
  className?: string;
  headingText?: string;
  setIsLocationValid?: (isValid: boolean) => void;
  onSelectSuggestion?: (value: string) => void;
}

const LocationInput: React.FC<Props> = ({
  tempLocation,
  setTempLocation,
  onChange = () => {},
  className = "",
  headingText = "Where to?",
  setIsLocationValid,
  onSelectSuggestion,
}) => {
  const [inputValue, setInputValue] = useState(tempLocation || ""); // Estado local solo para el input
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);

  // Usamos useTransition para optimizar actualizaciones
  const [isPending, startTransition] = useTransition();

  // Sincronizar inputValue con el estado tempLocation
  useEffect(() => {
    setInputValue(tempLocation);
  }, [tempLocation]); // Solo cuando tempLocation cambie, actualizamos el inputValue

  const mockLocations = ["Sevilla", "Málaga", "La Algaba, Sevilla"];

  // Filtrar sugerencias con debounce
  const fetchSuggestions = (input: string) => {
    const filtered = mockLocations.filter((loc) =>
      loc.toLowerCase().includes(input.toLowerCase())
    );
    setSuggestions(filtered);
  };

  // Manejar el cambio en el input
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value;
    setInputValue(newValue);  // Actualizamos solo el estado local del input
    setTempLocation(newValue);  // Actualizamos el estado temporal global

    // Manejar debounce para evitar múltiples llamadas a fetchSuggestions
    if (typingTimeout) clearTimeout(typingTimeout);
    const timeout = setTimeout(() => {
      startTransition(() => {
        fetchSuggestions(newValue); // Optimización usando startTransition
      });
    }, 500);
    setTypingTimeout(timeout);
  };

  const handleSelectSuggestion = (item: string) => {
    setInputValue(item);  // Actualizamos el input con la sugerencia seleccionada
    setTempLocation(item); // Actualizamos el estado temporal global
    if (setIsLocationValid) setIsLocationValid(true); // Validación de la ubicación
    if (onSelectSuggestion) onSelectSuggestion(item);
    setSuggestions([]); // Limpiar las sugerencias después de seleccionar
  };

  return (
    <div className={`${className}`}>
      <div className="p-5">
        <span className="font-semibold text-xl">{headingText}</span>
        <div className="relative mt-5">
          <input
            className="w-full bg-transparent border px-4 py-3 rounded-xl"
            placeholder="Destino"
            value={inputValue} // Usamos inputValue para sincronizar el estado local
            onChange={handleInputChange} // Manejamos el cambio
          />
          <MagnifyingGlassIcon className="w-5 h-5 absolute right-4 top-1/2 transform -translate-y-1/2" />
        </div>
        {suggestions.length > 0 && (
          <div className="mt-3">
            {suggestions.map((item, index) => (
              <div
                key={index}
                className="py-2 flex items-center cursor-pointer"
                onClick={() => handleSelectSuggestion(item)}
              >
                <MapPinIcon className="w-5 h-5 text-neutral-500" />
                <span className="ml-3">{item}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationInput;
